import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Check } from 'lucide-react';
import { toast } from 'sonner';
import { CoaDialog } from './CoaDialog';

/** Build the subcategory chip + size line shown above the product name. */
function deriveLabels(product) {
  const isPeptide = product.product_types?.includes('peptide');
  const isEquipment = product.product_types?.includes('equipment') && !isPeptide;
  const subcategoryLabel = isEquipment
    ? 'Equipment'
    : (product.tags?.[0] || product.category || 'Peptide');
  const variantSizes = (product.variants || [])
    .filter((v) => v.visible !== false)
    .map((v) => v.label);
  const sizeLine = isPeptide && variantSizes.length > 1
    ? `Size: ${variantSizes.join(' / ')}`
    : product.weight;
  return { isPeptide, subcategoryLabel, sizeLine };
}

/** Lowest variant price (catalog cards show "From $X" when multi-variant). */
function lowestPrice(product) {
  return product.variants?.length > 0
    ? Math.min(...product.variants.map((v) => v.price))
    : product.price;
}

function PriceTag({ product }) {
  const price = lowestPrice(product);
  const label = product.variants?.length > 1 ? `From $${price.toFixed(2)}` : `$${price.toFixed(2)}`;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span
        className="font-heading font-bold text-lg text-foreground whitespace-nowrap"
        data-testid={`price-${product.id}`}
      >
        {label}
      </span>
      {product.stock <= 0 && (
        <span className="text-xs font-medium text-red-500" data-testid={`stock-${product.id}`}>
          Out of stock
        </span>
      )}
    </div>
  );
}

function AddToCartButton({ product }) {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Please sign in to add items to your cart'); return; }
    if (product.stock <= 0) { toast.error('Out of stock'); return; }
    setAdding(true);
    const defaultVariant = product.variants?.[0]?.label || null;
    const result = await addToCart(product.id, 1, defaultVariant);
    if (result.success) toast.success(`${product.name} added to cart`);
    else toast.error(result.error);
    setTimeout(() => setAdding(false), 1200);
  };

  return (
    <Button
      size="sm"
      variant={adding ? 'secondary' : 'default'}
      onClick={handle}
      disabled={adding || product.stock <= 0}
      data-testid={`add-to-cart-${product.id}`}
      className={`gap-1.5 text-xs flex-shrink-0 ${
        !adding ? 'bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white' : ''
      }`}
    >
      {adding ? <Check className="h-3.5 w-3.5" /> : <ShoppingCart className="h-3.5 w-3.5" />}
      {adding ? 'Added' : 'Add'}
    </Button>
  );
}

export default function ProductCard({ product, index = 0 }) {
  const { isPeptide, subcategoryLabel, sizeLine } = deriveLabels(product);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link
        to={`/product/${product.id}`}
        className="group block"
        data-testid={`product-card-${product.id}`}
      >
        <div className="bg-card border border-border rounded-lg overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
          <div className="relative aspect-square bg-slate-50 overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {product.featured && (
              <Badge
                className="absolute top-3 left-3 bg-[hsl(210,45%,40%)] text-white text-xs"
                data-testid={`featured-badge-${product.id}`}
              >
                Featured
              </Badge>
            )}
            {isPeptide && <CoaDialog product={product} />}
          </div>
          <div className="p-4">
            <p
              className="text-xs text-muted-foreground uppercase tracking-wider font-semibold truncate"
              data-testid={`subcategory-${product.id}`}
            >
              {subcategoryLabel}
            </p>
            <h3 className="font-heading font-semibold text-foreground text-base mt-1">{product.name}</h3>
            <div className="mt-1 space-y-0.5">
              <p className="text-xs text-muted-foreground" data-testid={`weight-${product.id}`}>
                {sizeLine}
              </p>
              {product.form && (
                <p className="text-xs text-muted-foreground" data-testid={`form-${product.id}`}>
                  {product.form}
                </p>
              )}
            </div>
            {isPeptide && (
              <div
                className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
                data-testid={`bulk-callout-${product.id}`}
              >
                <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3" aria-hidden="true">
                  <path d="M2 8l3 3 9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Bulk save up to 15%
              </div>
            )}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border gap-2">
              <PriceTag product={product} />
              <AddToCartButton product={product} />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
